import { useParams, useLocation } from 'react-router-dom';
import {
  useGPanelContactQuery,
  useLabelsQuery,
} from '../../gpanel-contacts-queries.ts';
import TextHoverCopy from '../../../../../shared/text-hover-copy.tsx';
import { useState } from 'react';
import GPanelContactsDeleteDialog from './gpanel-contacts-delete-dialog.tsx';
import GPanelContactsModal from './gpanel-contacts-form-page.tsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GPanelContactsKeys,
  setLabelsToContact,
} from '../../gpanel-contacts-api.ts';
import { Contact, ValueLabel } from '../../shared/types.ts';
import GpanelContactsAddLabelDialog from '../gpanel-contacts-labels/dialogs/gpanel-contacts-add-label-dialog.tsx';
import { useStore } from '../../../../state/zustand-store.ts';
import { Messages } from '../../../../../utils/message-constants.ts';
import TooltipWithPermission from '../../shared/components/tooltip-with-permission.tsx';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles.ts';
import { useGpanelContactsPermissions } from '../../shared/hooks/use-gpanel-contacts-permissions.tsx';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import SharedBackButton from '../../shared/components/shared-back-button.tsx';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button.tsx';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Cake from '@mui/icons-material/Cake';
import Check from '@mui/icons-material/Check';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import GroupWorkOutlined from '@mui/icons-material/GroupWorkOutlined';
import Label from '@mui/icons-material/Label';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import LocalPhoneOutlined from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import NoteOutlined from '@mui/icons-material/NoteOutlined';
import ViewAgendaOutlined from '@mui/icons-material/ViewAgendaOutlined';

interface PropertyRow {
  key: string;
  label: string;
  icon: React.ReactNode;
  values: ValueLabel[];
}

export function PropertyItem({
  icon,
  label,
  values,
}: {
  icon: React.ReactNode;
  label: string;
  values: ValueLabel[];
}) {
  return (
    <Stack
      key={label}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ marginBottom: '20px', width: 'fit-content' }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ width: 180, minWidth: 180, p: 0.5 }}
        alignItems="center"
      >
        {icon}
        <Typography>{label}</Typography>
      </Stack>
      <Stack spacing={0.5} sx={{ flexGrow: 1 }} alignItems="left">
        {values.map((v) => (
          <Box key={`${v.value}-${v.label}`} sx={{ whiteSpace: 'pre-line' }}>
            <TextHoverCopy
              clipboardValue={v.value}
              value={v.value + (v.label ? ` · ${v.label}` : '')}
              valueType={label}
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

export default function GPanelContactsDetails() {
  const { contactId = '' } = useParams<{ contactId: string }>();
  const contactIdValue = Number(contactId);
  const queryClient = useQueryClient();
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const setSnackbar = useStore((state) => state.setSnackbar);
  const { createEvent } = useAnalytics();
  const { changeLogRoles, loggedInUser } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const location = useLocation();

  const {
    data: contact,
    isLoading,
    isError,
  } = useGPanelContactQuery(contactId ?? '');
  const { data: labels = [], isFetching: isLabelsFetching } = useLabelsQuery();
  const setLabelsToContactMutation = useMutation({
    mutationFn: ({
      labelIds,
      labelNames,
    }: {
      labelIds: number[];
      labelNames: string[];
    }) => setLabelsToContact(contactId, labelIds, labelNames, changeLogMessage),
    onSuccess: () => {
      queryClient.setQueryData<Contact>(
        [GPanelContactsKeys.GPanelContact, contactId],
        (prev) =>
          prev
            ? {
                ...prev,
                labelIds: finalSelectedLabelIds,
              }
            : prev
      );

      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.LabelGroups],
      });
      queryClient.setQueryData<Contact[]>(
        [GPanelContactsKeys.OrganizationDirectoryContacts],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((c: Contact) => {
            if (String(c.id) === String(contactIdValue)) {
              return {
                ...c,
                labelIds: finalSelectedLabelIds,
              };
            }
            return c;
          });
        }
      );

      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated("Contact's label(s) were"),
      });

      setMenuAnchorEl(null);
      setChangeLogMessage('');
    },
    onError: () => {
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Updating("contact's label(s)"),
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.edit_contact_labels, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const isPending = setLabelsToContactMutation.isPending;

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuAnchorEl);

  const [openCreateLabelDialog, setOpenCreateLabelDialog] = useState(false);

  const [initialLabelIds, setInitialLabelIds] = useState<number[]>([]);
  const [selectedToApplyIds, setSelectedToApplyIds] = useState<number[]>([]);

  const handleOpenLabelMenu = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(e.currentTarget);
    setInitialLabelIds(contact?.labelIds || []);
    setSelectedToApplyIds([]);
  };

  const toggleLabelSelection = (labelId: number) => {
    setSelectedToApplyIds((prev) =>
      prev.includes(labelId)
        ? prev.filter((id) => id !== labelId)
        : [...prev, labelId]
    );
  };

  const finalSelectedLabelIds = labels
    .map((lbl) => lbl.id)
    .filter((id) =>
      selectedToApplyIds.includes(id)
        ? !initialLabelIds.includes(id)
        : initialLabelIds.includes(id)
    );

  const finalSelectedLabelNames = labels
    .filter((lbl) => finalSelectedLabelIds.includes(lbl.id))
    .map((lbl) => lbl.name);

  const handleApply = () => {
    if (isPending) return;

    // Always close the menu before mutation to avoid invalid anchorEl
    setMenuAnchorEl(null);

    if (
      !openChangeLogDialog &&
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.EditGpanelLabels.value
      )
    ) {
      setOpenChangeLogDialog(true);
      return;
    }

    const request = {
      labelIds: finalSelectedLabelIds,
      labelNames: finalSelectedLabelNames,
    };

    setLabelsToContactMutation.mutate(request);
  };

  if (isLoading)
    return (
      <Container sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" width="60%" />
      </Container>
    );

  if (isError || !contact) return <div>Error loading contact</div>;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const baseRows: PropertyRow[] = [
    {
      key: 'emails',
      label: 'Email',
      icon: <EmailOutlined color="primary" />,
      values:
        contact.emails?.map((e) => ({
          value: e.value,
          label: e.label ?? '',
        })) ?? [],
    },
    {
      key: 'phones',
      label: 'Phone',
      icon: <LocalPhoneOutlined color="primary" />,
      values:
        contact.phoneNumbers?.map((e) => ({
          value: e.value,
          label: e.label ?? '',
        })) ?? [],
    },
    {
      key: 'birthday',
      label: 'Birthdate',
      icon: <Cake color="primary" />,
      values: contact.birthday
        ? [{ value: formatDate(contact.birthday), label: '' }]
        : [],
    },
    {
      key: 'addresses',
      label: 'Address',
      icon: <LocationOnOutlined color="primary" />,
      values:
        contact.addresses?.map(
          ({
            country,
            street,
            poBox,
            street2,
            city,
            state,
            zipCode,
            label,
          }) => {
            const line1 = street;
            const line2 = poBox;
            const line3 =
              [street2, city, state].filter(Boolean).join(', ') +
              (zipCode ? ` ${zipCode}` : '');
            const line4 = country;

            const value = [line1, line2, line3, line4]
              .filter(Boolean)
              .join('\n');

            return { value, label: label ?? '' };
          }
        ) ?? [],
    },
    {
      key: 'significantDates',
      label: 'Significant Date',
      icon: <EventOutlined color="primary" />,
      values:
        contact.significantDates?.map(({ date, notes }) =>
          notes
            ? { value: `${formatDate(date)} (${notes})`, label: '' }
            : { value: formatDate(date), label: '' }
        ) ?? [],
    },
    {
      key: 'relatedPeople',
      label: 'Related Person',
      icon: <GroupWorkOutlined color="primary" />,
      values:
        contact.relatedPeople?.map(({ value, label }) =>
          label
            ? { value: `${value} (${label})`, label: '' }
            : { value: value, label: '' }
        ) ?? [],
    },
    {
      key: 'websites',
      label: 'Website',
      icon: <LinkOutlined color="primary" />,
      values:
        contact.websites?.map((e) => ({
          value: e.value,
          label: e.label ?? '',
        })) ?? [],
    },
    {
      key: 'customField',
      label: 'Custom Field',
      icon: <ViewAgendaOutlined color="primary" />,
      values:
        contact.customFields?.map((e) => ({
          value: e.value,
          label: e.label ?? '',
        })) ?? [],
    },
  ];

  const notesRow: PropertyRow = {
    key: 'notes',
    label: 'Notes',
    icon: <NoteOutlined color="primary" />,
    values: contact.notes ? [{ value: contact.notes, label: '' }] : [],
  };

  const handleUpdateContact = (updated: Contact) => {
    queryClient.setQueryData(
      [GPanelContactsKeys.GPanelContact, contactId],
      updated
    );
    setEditOpen(false);
  };

  const activeLabel =
    contact.labelIds
      ?.map((labelId: number) =>
        labels.find(
          (existingLabel) =>
            existingLabel.id === labelId && existingLabel.trashed === false
        )
      )
      .filter(Boolean) || [];

  const filteredRows = baseRows.filter(
    (row) => row.values.length > 0 && row.values.some((v) => v.value?.trim())
  );

  const propertyRows: PropertyRow[] = [...filteredRows, notesRow].filter(
    (row) => row.values.filter(Boolean).length
  );

  const getBackPath = () => {
    const pathSegments = location.pathname.split('/');
    const labelIndex = pathSegments.indexOf('organization-labels');
    const labelId = pathSegments[labelIndex + 1] || location.state?.fromLabelId;

    return labelId
      ? `/gpanel-contacts/organization-labels/${labelId}`
      : '/gpanel-contacts/gpanel-directory';
  };

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        height: '100%',
        p: 1,
      }}
      maxWidth={false}
    >
      <Stack spacing={4} width={1}>
        <SharedBackButton to={getBackPath()} />

        <Stack direction="row" spacing={4} width={1}>
          <Stack direction="row" spacing={2} width={1}>
            <Stack direction="column" width={1}>
              <Typography variant="h5">
                {contact.fileAs?.trim() ? (
                  contact.fileAs
                ) : (
                  <>
                    {contact.prefix} {contact.firstName} {contact.middleName}{' '}
                    {contact.lastName} {contact.suffix}{' '}
                    {contact.nickname && `(${contact.nickname})`}
                  </>
                )}
              </Typography>

              <Typography color="textDisabled">
                {[
                  contact.phoneticFirst,
                  contact.phoneticMiddle,
                  contact.phoneticLast,
                  contact.companyJobTitle,
                  contact.companyDepartment,
                  contact.companyName,
                ]
                  .filter((e) => e)
                  .join(' - ')}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems={'start'}>
            <TooltipWithPermission
              hasPermission={PERMISSIONS.editContactDetails}
              permissionName={GPanelContactsPermissions.EditContactDetails.name}
              renderElement={(hasPermission) => (
                <SharedIconButton
                  variant="edit"
                  size="medium"
                  disabled={!hasPermission}
                  sx={{ height: 40, width: 40 }}
                  onClick={() => setEditOpen(true)}
                  disableTooltip
                />
              )}
            />
            <TooltipWithPermission
              hasPermission={PERMISSIONS.trashContact}
              permissionName={GPanelContactsPermissions.TrashContact.name}
              renderElement={(hasPermission) => (
                <SharedIconButton
                  variant="delete"
                  size="medium"
                  disabled={!hasPermission}
                  sx={{ height: 40, width: 40 }}
                  onClick={() => setDeleteOpen(true)}
                  disableTooltip
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={'16px'} width={1}>
          {contact.emails?.length ? (
            <Tooltip arrow title="Send Email">
              <IconButton
                size="medium"
                disabled={!contact.emails?.length || !contact.emails[0].value}
                sx={{
                  borderRadius: '50%',
                  backgroundColor: '#7272B5',
                  color: '#fff',
                }}
                href={`https://mail.google.com/mail/u/?authuser=${loggedInUser}&view=cm&fs=1&tf=1&to=${contact.emails?.[0].value}`}
                target="_blank"
              >
                <EmailOutlined fontSize="medium" />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}

          <Stack
            sx={{
              width: 1,
              height: '1px',
              border: '1px solid #7272B5',
            }}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} width={1}>
          {activeLabel &&
            activeLabel.length > 0 &&
            activeLabel.map((lbl) => {
              const label = lbl?.name ?? '';
              const isLong = label.length > 40;
              const displayLabel = isLong ? `${label.slice(0, 40)}...` : label;

              const chip = (
                <Chip
                  key={lbl?.id}
                  label={displayLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ width: 'fit-content' }}
                />
              );

              return isLong ? (
                <Tooltip key={lbl?.id} title={label}>
                  {chip}
                </Tooltip>
              ) : (
                chip
              );
            })}
          <TooltipWithPermission
            hasPermission={PERMISSIONS.modifyGpanelContactLabelMembers}
            title={
              !contact.labelIds ||
              contact.labelIds.length === 0 ||
              activeLabel.length === 0
                ? 'Add Label'
                : 'Edit Labels'
            }
            permissionName={
              GPanelContactsPermissions.ModifyGpanelContactLabelMembers.name
            }
            renderElement={(hasPermission) => (
              <>
                {(!contact.labelIds ||
                  contact.labelIds.length === 0 ||
                  activeLabel.length === 0) && (
                  <Chip
                    label="Label"
                    size="small"
                    color="primary"
                    variant="outlined"
                    disabled={!hasPermission}
                    icon={<Add fontSize="small" />}
                    sx={{ width: 'fit-content', cursor: 'pointer' }}
                    onClick={handleOpenLabelMenu}
                  />
                )}

                {Array.isArray(contact.labelIds) &&
                  contact.labelIds.length > 0 &&
                  activeLabel.length > 0 && (
                    <SharedIconButton
                      variant="edit"
                      size="small"
                      sx={{ height: 30, width: 30 }}
                      disabled={!hasPermission}
                      onClick={handleOpenLabelMenu}
                      disableTooltip
                    />
                  )}
              </>
            )}
          />
        </Stack>

        <Stack
          spacing={1}
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
            marginBottom: '20px !important',
            pl: 3,
          }}
        >
          {propertyRows.map((row) => (
            <PropertyItem
              key={row.key}
              icon={row.icon}
              label={row.label}
              values={row.values}
            />
          ))}
        </Stack>
      </Stack>

      <Menu
        anchorEl={menuAnchorEl}
        open={
          openMenu &&
          !!menuAnchorEl &&
          // Only open if anchorEl is still in the document
          (typeof menuAnchorEl === 'object' && 'ownerDocument' in menuAnchorEl
            ? menuAnchorEl.ownerDocument.body.contains(menuAnchorEl)
            : true)
        }
        onClose={() => {
          if (isPending) return;
          setMenuAnchorEl(null);
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              py: 0,
            },
          },
          paper: {
            style: {
              maxHeight: 400,
              overflow: 'hidden',
              maxWidth: 280,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 320 }}>
          {isLabelsFetching && (
            <MenuItem disabled sx={{ opacity: 0.6 }}>
              Loading…
            </MenuItem>
          )}

          {labels.length > 0 &&
            labels
              .filter((label) => !label.trashed)
              .map((lbl) => {
                const isInInitial = initialLabelIds.includes(lbl.id);
                const isToggled = selectedToApplyIds.includes(lbl.id);
                const isSelected = isToggled ? !isInInitial : isInInitial;

                return (
                  <MenuItem
                    key={lbl.id}
                    onClick={() => toggleLabelSelection(lbl.id)}
                  >
                    <Label sx={{ mr: 1, opacity: 0.54 }} />

                    {lbl.name.length > 20 ? (
                      <Tooltip title={lbl.name}>
                        <Typography component="span">{`${lbl.name.slice(
                          0,
                          20
                        )}...`}</Typography>
                      </Tooltip>
                    ) : (
                      lbl.name
                    )}

                    {isSelected ? (
                      <Check sx={{ ml: 1, opacity: 0.54 }} />
                    ) : (
                      <Check sx={{ ml: 1, visibility: 'hidden' }} />
                    )}
                  </MenuItem>
                );
              })}
        </Box>

        <MenuItem divider sx={{ pointerEvents: 'none', opacity: 0.6, p: 0 }} />

        {selectedToApplyIds.length > 0 ? (
          <MenuItem onClick={handleApply}>
            {isPending ? (
              <CircularProgress sx={{ mx: 'auto' }} size={25} />
            ) : (
              <>
                <Check fontSize="small" sx={{ mr: 1 }} />
                Apply
              </>
            )}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setOpenCreateLabelDialog(true);
            }}
          >
            <Add fontSize="small" sx={{ mr: 1 }} />
            Create New Label
          </MenuItem>
        )}
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleApply}
          mutateLoading={isPending}
        />
      </Menu>

      {openCreateLabelDialog && (
        <GpanelContactsAddLabelDialog
          handleClose={() => setOpenCreateLabelDialog(false)}
        />
      )}

      {editOpen && (
        <GPanelContactsModal
          contact={contact ?? undefined}
          open={true}
          handleClose={() => setEditOpen(false)}
          handleUpdate={handleUpdateContact}
        />
      )}

      {deleteOpen && (
        <GPanelContactsDeleteDialog
          handleClose={() => setDeleteOpen(false)}
          contactIds={[contactIdValue]}
        />
      )}
    </Container>
  );
}
